import { createRoot, createSignal } from 'solid-js';
import { defaultStationId, radioStations, type RadioStation } from '~/config/radio';
import {
  getCurrentStreamUrl,
  hasAudio,
  loadStream,
  onAudioEvent,
  pauseAudio,
  playAudio,
  setMutedState,
  setVolume as setAudioVolume,
  stopAudio,
} from '~/lib/audioController';

export type RadioStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

const LS_STATION = 'concept13.radio.station';
const LS_VOLUME = 'concept13.radio.volume';
const LS_MUTED = 'concept13.radio.muted';
const LS_COLLAPSED = 'concept13.radio.collapsed';

const lsAvailable = (): boolean => {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
};

function lsGet(key: string, fallback: string): string {
  if (!lsAvailable()) return fallback;
  try {
    return window.localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

function lsSet(key: string, value: string) {
  if (!lsAvailable()) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* quota or private mode */
  }
}

function pickStation(id: string): RadioStation {
  return radioStations.find((s) => s.id === id) ?? radioStations[0];
}

function clampVolume(raw: string, fallback = 0.7): number {
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(1, n));
}

function createStore() {
  const initialStationId = pickStation(lsGet(LS_STATION, defaultStationId)).id;
  const initialVolume = clampVolume(lsGet(LS_VOLUME, '0.7'));
  const initialMuted = lsGet(LS_MUTED, 'false') === 'true';
  const initialCollapsed = lsGet(LS_COLLAPSED, 'true') === 'true';

  const [stationId, setStationId] = createSignal(initialStationId);
  const [volume, setVolumeSig] = createSignal(initialVolume);
  const [muted, setMutedSig] = createSignal(initialMuted);
  const [collapsed, setCollapsedSig] = createSignal(initialCollapsed);
  const [status, setStatus] = createSignal<RadioStatus>('idle');
  const [error, setError] = createSignal<string | null>(null);

  // Пользователь хотя бы один раз нажал Play в текущей сессии.
  // Используется при переключении станции — продолжаем играть только
  // если радио уже было активировано.
  const [userActivated, setUserActivated] = createSignal(false);

  let listenersAttached = false;

  function attachListeners() {
    if (listenersAttached || !hasAudio()) return;
    listenersAttached = true;

    onAudioEvent('loadstart', () => {
      // не затираем error → user может видеть «Повторить»
      if (status() !== 'error') setStatus('loading');
    });
    onAudioEvent('canplay', () => {
      // canplay не означает, что играем — статус выставляется на playing
      // отдельным событием. Если мы на паузе — оставляем как есть.
    });
    onAudioEvent('playing', () => {
      setStatus('playing');
      setError(null);
    });
    onAudioEvent('pause', () => {
      if (status() !== 'error') setStatus('paused');
    });
    onAudioEvent('waiting', () => {
      if (status() !== 'error') setStatus('loading');
    });
    onAudioEvent('stalled', () => {
      if (status() !== 'error') setStatus('loading');
    });
    onAudioEvent('error', () => {
      setStatus('error');
      setError('Не удалось подключиться к эфиру. Попробуйте ещё раз.');
    });
  }

  function currentStation(): RadioStation {
    return pickStation(stationId());
  }

  async function play() {
    const s = currentStation();
    if (!s.streamUrl) {
      setStatus('error');
      setError(
        'Поток радио ещё не настроен. Укажите streamUrl в src/config/radio.ts.',
      );
      return;
    }
    if (!hasAudio()) return;

    attachListeners();
    setUserActivated(true);
    setError(null);

    setAudioVolume(muted() ? 0 : volume());
    setMutedState(muted());

    setStatus('loading');
    try {
      if (getCurrentStreamUrl() !== s.streamUrl) {
        await loadStream(s.streamUrl);
      }
      await playAudio();
    } catch (e) {
      setStatus('error');
      const msg = e instanceof Error ? e.message : 'Не удалось запустить эфир.';
      setError(`Не удалось подключиться к эфиру. ${msg}`);
    }
  }

  function pause() {
    pauseAudio();
    setStatus('paused');
  }

  async function togglePlay() {
    if (status() === 'playing') {
      pause();
    } else {
      await play();
    }
  }

  async function selectStation(id: string) {
    if (stationId() === id) return;
    const target = pickStation(id);
    setStationId(target.id);
    lsSet(LS_STATION, target.id);

    const shouldResume =
      userActivated() &&
      (status() === 'playing' || status() === 'loading' || status() === 'paused');

    // Прерываем текущую загрузку/поток до подмены src
    stopAudio();
    setStatus('idle');
    setError(null);

    if (shouldResume) {
      await play();
    }
  }

  function changeVolume(v: number) {
    const clamped = Math.max(0, Math.min(1, v));
    setVolumeSig(clamped);
    lsSet(LS_VOLUME, String(clamped));
    if (!hasAudio()) return;
    if (!muted()) setAudioVolume(clamped);
    if (clamped === 0) {
      // двигатели браузера снимают mute при ползунке вверх — это ок
    }
  }

  function toggleMute() {
    const next = !muted();
    setMutedSig(next);
    lsSet(LS_MUTED, String(next));
    if (!hasAudio()) return;
    setMutedState(next);
    setAudioVolume(next ? 0 : volume());
  }

  function toggleCollapsed() {
    const next = !collapsed();
    setCollapsedSig(next);
    lsSet(LS_COLLAPSED, String(next));
  }

  function setCollapsed(next: boolean) {
    setCollapsedSig(next);
    lsSet(LS_COLLAPSED, String(next));
  }

  async function retry() {
    setError(null);
    await play();
  }

  return {
    // state
    stationId,
    station: currentStation,
    stations: radioStations,
    volume,
    muted,
    collapsed,
    status,
    error,
    userActivated,
    // actions
    play,
    pause,
    togglePlay,
    selectStation,
    changeVolume,
    toggleMute,
    toggleCollapsed,
    setCollapsed,
    retry,
  };
}

// Один экземпляр на всё приложение — модуль кэшируется, signals
// инициализируются один раз. Маршрутные переходы их не сбрасывают.
export const radioStore = createRoot(() => createStore());
